import React, {
    FC,
    memo,
    useCallback,
    useState,
} from 'react';

import { message } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';


import PasswordView from './PasswordView';
import styles from './RecipientPassword.module.scss';
import PasswordEdit from '@/components/Common/PasswordEdit';

const MIN_PASSWORD_LENGTH = 8;
const nameSpace = 'sharingBlock.recipientsList';

export interface ChangePasswordProps {
    changePassword?: (password: string) => Promise<void>;
}

interface RecipientPasswordProps extends ChangePasswordProps {
    maskedPassword: string | null;
    isSearching: boolean;
}

const RecipientPassword: FC<RecipientPasswordProps> = ({
    maskedPassword,
    isSearching,
    changePassword,
}) => {
    const { t } = useTranslation();
    const [isEditModeEnabled, setIsEditModeEnabled] = useState<boolean>(false);
    const [isSaving, setIsSaving] = useState<boolean>(false);

    const enableEditMode = useCallback(() => setIsEditModeEnabled(true), []);
    const disableEditMode = useCallback(() => {
        setIsEditModeEnabled(false);
    }, []);

    const handleSave = async (password: string): Promise<void> => {
        if (password.length < MIN_PASSWORD_LENGTH) {
            message.error(t(`${nameSpace}.passwordMinLength`, { length: MIN_PASSWORD_LENGTH }));
            return;
        }
        setIsSaving(true);
        try {
            await changePassword?.(password);
            disableEditMode();
        } finally {
            setIsSaving(false);
        }
    };

    if (isSearching) {
        return (
            <span className={styles['loader-wrapper']}>
                <LoadingOutlined className={styles.loader} spin />
            </span>
        );
    }

    return (
        <div className={styles['password-wrapper']}>
            {isEditModeEnabled ? (
                <PasswordEdit
                    onSave={handleSave}
                    onCancel={disableEditMode}
                    isLoading={isSaving}
                />
            ) : (
                <PasswordView
                    maskedPassword={maskedPassword}
                    enableEditMode={enableEditMode}
                />
            )}
        </div>
    );
};

export default memo(RecipientPassword);

